import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from 'react';
import {
  Typography, Select, DatePicker, Button, Space, Spin, Empty, Checkbox,
} from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';

// ── time helpers ──────────────────────────────────────────────────────────────

function toMins(str) {
  if (!str) return null;
  const t = str.slice(-8);
  const [h, m, s = '0'] = t.split(':');
  const v = +h * 60 + +m + +s / 60;
  return isNaN(v) ? null : v;
}

function fmtHM(str) {
  if (!str) return '?';
  return str.slice(-8).slice(0, 5);
}

function fmtDur(secs) {
  if (secs == null || secs < 0) return '';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

// HalfHourOfDay integer (e.g. 730) → minutes from midnight (e.g. 450)
function hhToMins(hh) {
  return Math.floor(hh / 100) * 60 + (hh % 100);
}

// ── color helpers ─────────────────────────────────────────────────────────────

function stateColor(state) {
  if (!state) return '#8c8c8c';
  const s = state.toLowerCase();
  if (s === 'available')                         return '#52c41a';
  if (s === 'logged in')                         return '#1677ff';
  if (s === 'logged out')                        return '#434343';
  if (s === 'unavailable')                       return '#d9d9d9';
  if (s.includes('break'))                       return '#fa8c16';
  if (s.includes('lunch') || s.includes('meal')) return '#ff85c2';
  if (s.includes('refused'))                     return '#ff4d4f';
  if (s.includes('technical'))                   return '#722ed1';
  if (s.startsWith('inbound'))                   return '#13c2c2';
  if (s.startsWith('outbound'))                  return '#2f54eb';
  return '#8c8c8c';
}

function stateFg(state) {
  const light = ['#52c41a', '#ff85c2', '#fa8c16', '#d9d9d9', '#13c2c2'];
  return light.includes(stateColor(state)) ? '#262626' : '#ffffff';
}

// ── layout constants ──────────────────────────────────────────────────────────

const LABEL_W   = 190;
const PAD_R     = 20;
const AXIS_H    = 46;
const ROW_H     = 42;
const ROW_GAP   = 10;
const AGENT_H   = 24;   // height of agent name band
const MET_H     = 28;   // height of each campaign metric row
const MET_GAP   = 4;
const CR        = 4;
const MIN_W     = 1400;

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const ampm = i < 12 ? 'AM' : 'PM';
  const h12  = i === 0 ? 12 : i > 12 ? i - 12 : i;
  return { label: `${h12}:00 ${ampm}`, value: i };
});

const LEGEND_ITEMS = [
  { label: 'Schedule',        color: '#91caff', alpha: 0.75, border: true },
  { label: 'Punches',         color: '#52c41a', alpha: 0.65 },
  { label: 'Available',       color: '#52c41a' },
  { label: 'Logged In',       color: '#1677ff' },
  { label: 'Inbound',         color: '#13c2c2' },
  { label: 'Outbound',        color: '#2f54eb' },
  { label: 'Break',           color: '#fa8c16' },
  { label: 'Lunch/Meal',      color: '#ff85c2' },
  { label: 'Unavailable',     color: '#d9d9d9', border: true },
  { label: 'Refused Contact', color: '#ff4d4f' },
  { label: 'Technical Issue', color: '#722ed1' },
  { label: 'Logged Out',      color: '#434343' },
];

// ── component ─────────────────────────────────────────────────────────────────

export default function TeamTimelinePage() {
  const currentUser = useContext(UserContext);
  const isAdmin     = currentUser?.Role === 'admin';
  const isQaAdmin   = currentUser?.Role === 'qa_admin';

  const [allTeams,      setAllTeams]      = useState([]);
  const [teamId,        setTeamId]        = useState(null);
  const [allJobs,       setAllJobs]       = useState([]);
  const [jobIds,        setJobIds]        = useState([]);
  const [date,          setDate]          = useState(dayjs().subtract(1, 'day'));
  const [startHour,     setStartHour]     = useState(7);
  const [endHour,       setEndHour]       = useState(19);
  const [hidePunches,       setHidePunches]       = useState(false);
  const [hideSchedule,      setHideSchedule]      = useState(false);
  const [hideNonScheduled,   setHideNonScheduled]   = useState(true);
  const [hideInTraining,     setHideInTraining]     = useState(true);
  const [showOnlyBreakLunch, setShowOnlyBreakLunch] = useState(false);

  const [agents,        setAgents]        = useState([]);
  const [timelineMap,   setTimelineMap]   = useState({});
  const [loading,       setLoading]       = useState(false);

  const [campaign,        setCampaign]        = useState(null);
  const [campaignOpts,    setCampaignOpts]    = useState([]);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [erlangData,      setErlangData]      = useState([]);

  const filteredCampaignOpts = useMemo(() => {
    const role  = currentUser?.Role;
    const camps = currentUser?.Campaigns || [];
    if (!currentUser || ['admin', 'qa_admin'].includes(role) || camps.length === 0) return campaignOpts;
    const lower = camps.map(c => c.toLowerCase());
    return campaignOpts.filter(opt => lower.some(vc => opt.value.toLowerCase().includes(vc)));
  }, [campaignOpts, currentUser]);

  const [svgW,    setSvgW]    = useState(900);
  const [tooltip, setTooltip] = useState(null);

  const containerRef = useRef(null);

  // ── Track container width ─────────────────────────────────────────────────

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setSvgW(e.contentRect.width || 900));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Load reference data ───────────────────────────────────────────────────

  useEffect(() => {
    api.get('/api/teams', { params: { no_client_users: 1 } }).then(r => setAllTeams(r.data)).catch(() => {});
    api.get('/api/jobs').then(r => setAllJobs(r.data || [])).catch(() => {});
    setCampaignLoading(true);
    api.get('/api/erlang-stats/campaigns')
      .then(r => setCampaignOpts(r.data.map(c => ({ label: c, value: c }))))
      .catch(() => {})
      .finally(() => setCampaignLoading(false));
  }, []);

  const teamOptions = (() => {
    // qa_admin follows Visible Teams like everyone else; only admin sees all.
    if (isAdmin) return allTeams;
    const vis = currentUser?.VisibleTeams || [];
    return vis.length ? vis : allTeams;
  })();

  useEffect(() => {
    if (teamId == null && teamOptions.length && currentUser) {
      const myTeamId = currentUser.TeamID;
      if (myTeamId && teamOptions.some(t => t.ID === myTeamId)) setTeamId(myTeamId);
      else setTeamId(teamOptions[0]?.ID ?? null);
    }
  }, [teamOptions, teamId, currentUser]);

  useEffect(() => {
    if (jobIds.length === 0 && allJobs.length) {
      const agentJob = allJobs.find(j => j.IsActive && j.JobName?.toLowerCase() === 'agent');
      if (agentJob) setJobIds([agentJob.JobID]);
      else {
        const adherence = allJobs.filter(j => j.InAdherence && j.IsActive).map(j => j.JobID);
        if (adherence.length) setJobIds(adherence);
      }
    }
  }, [allJobs, jobIds.length]);

  // ── Load agent + timeline data ────────────────────────────────────────────

  const load = useCallback(async () => {
    if (!teamId || !date) { setAgents([]); setTimelineMap({}); return; }
    setLoading(true);
    try {
      const params = { team_ids: teamId.toString(), date: date.format('YYYY-MM-DD') };
      if (jobIds.length) params.job_ids = jobIds.join(',');
      const res       = await api.get('/api/schedule', { params });
      const agentList = res.data.agents || [];
      setAgents(agentList);

      const results = await Promise.allSettled(
        agentList.map(a =>
          api.get('/api/agent/ccaas-timeline', {
            params: { user_id: a.ID, date: date.format('YYYY-MM-DD') },
          }).then(r => ({ id: a.ID, data: r.data }))
        )
      );
      const map = {};
      for (const r of results) {
        if (r.status === 'fulfilled') map[r.value.id] = r.value.data;
      }
      setTimelineMap(map);
    } catch {
      setAgents([]);
      setTimelineMap({});
    } finally {
      setLoading(false);
    }
  }, [teamId, jobIds, date]);

  // ── Load erlang data ──────────────────────────────────────────────────────

  useEffect(() => {
    if (!campaign || !date) { setErlangData([]); return; }
    const ds = date.format('YYYY-MM-DD');
    api.get('/api/erlang-stats', { params: { start_date: ds, end_date: ds, campaign } })
      .then(r => setErlangData(r.data))
      .catch(() => setErlangData([]));
  }, [campaign, date]);

  const erlangByHH = useMemo(() => {
    const map = {};
    erlangData.forEach(r => { map[r.HalfHourOfDay] = r; });
    return map;
  }, [erlangData]);

  // ── Derived geometry ──────────────────────────────────────────────────────

  const visibleRows = useMemo(() => {
    const rows = [];
    if (!hideSchedule) rows.push({ key: 'schedule', label: 'Schedule' });
    if (!hidePunches)  rows.push({ key: 'punches',  label: 'Punches'  });
    rows.push({ key: 'states', label: 'CCaaS States' });
    return rows;
  }, [hideSchedule, hidePunches]);

  // The from/to selectors define the view window; data outside it is clipped naturally by SVG
  const rangeMin = startHour * 60;
  const rangeMax = endHour   * 60;

  const effectiveW = Math.max(MIN_W, svgW);
  const drawW      = Math.max(100, effectiveW - LABEL_W - PAD_R);
  const span       = Math.max(1, rangeMax - rangeMin);
  const toX        = useCallback(m => LABEL_W + ((m - rangeMin) / span) * drawW, [rangeMin, span, drawW]);

  const ticks = useMemo(() => {
    const out = [];
    for (let m = Math.ceil(rangeMin / 15) * 15; m <= rangeMax; m += 15) out.push(m);
    return out;
  }, [rangeMin, rangeMax]);

  // Campaign section height (shown in sticky top SVG)
  const hasCampaign   = campaign && erlangData.length > 0;
  const campaignBlockH = hasCampaign ? AGENT_H + 6 * (MET_H + MET_GAP) : 0;
  const topSvgH        = AXIS_H + campaignBlockH;

  // Apply hide-non-scheduled and hide-in-training filters (client-side, after data loads)
  const displayAgents = agents.filter(a => {
    const schedule = timelineMap[a.ID]?.schedule || [];
    if (hideNonScheduled && schedule.length === 0) return false;
    if (hideInTraining && schedule.length > 0 &&
        schedule.every(s => (s.DepartmentName || '').toLowerCase().includes('training'))) return false;
    return true;
  });

  // Agent section height (scrollable SVG)
  const agentBlockH = AGENT_H + visibleRows.length * (ROW_H + ROW_GAP);
  const bottomSvgH  = Math.max(50, displayAgents.length * agentBlockH + 16);

  // ── Tooltip ───────────────────────────────────────────────────────────────

  const showTip = (e, lines) => setTooltip({ x: e.clientX, y: e.clientY, lines });
  const moveTip = (e) => setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null);
  const hideTip = () => setTooltip(null);

  // ── Renderers ─────────────────────────────────────────────────────────────

  const renderTimeAxis = () => (
    <g>
      {ticks.map(m => {
        const x = toX(m); const isHour = m % 60 === 0; const isHalf = !isHour && m % 30 === 0;
        return <line key={m} x1={x} y1={AXIS_H - 12} x2={x} y2={topSvgH}
          stroke={isHour ? '#d9d9d9' : isHalf ? '#e0e0e0' : '#f0f0f0'}
          strokeWidth={isHour ? 1 : isHalf ? 0.75 : 0.5} />;
      })}
      {ticks.filter(m => m % 60 === 0).map(m => (
        <line key={`tk${m}`} x1={toX(m)} y1={AXIS_H - 12} x2={toX(m)} y2={AXIS_H - 4} stroke="#bfbfbf" strokeWidth={1} />
      ))}
      {ticks.filter(m => m % 30 === 0 && m % 60 !== 0).map(m => (
        <line key={`tk30${m}`} x1={toX(m)} y1={AXIS_H - 8} x2={toX(m)} y2={AXIS_H - 4} stroke="#d0d0d0" strokeWidth={1} />
      ))}
      {ticks.filter(m => m % 60 === 0).map(m => {
        const h = Math.floor(m / 60);
        return <text key={`lb${m}`} x={toX(m)} y={AXIS_H - 15} textAnchor="middle" fontSize={12} fill="#8c8c8c">
          {`${h % 12 || 12}${h % 24 < 12 ? 'am' : 'pm'}`}
        </text>;
      })}
      {ticks.filter(m => m % 30 === 0 && m % 60 !== 0).map(m => (
        <text key={`lb30${m}`} x={toX(m)} y={AXIS_H - 15} textAnchor="middle" fontSize={9} fill="#bfbfbf">:30</text>
      ))}
    </g>
  );

  const renderCampaignRows = () => {
    if (!hasCampaign) return null;
    let y = AXIS_H;
    const els = [];

    // Campaign name band
    els.push(
      <g key="camp-hdr">
        <rect x={0} y={y} width={effectiveW} height={AGENT_H} fill="#f9f0ff" />
        <text x={LABEL_W / 2} y={y + AGENT_H / 2 + 4} textAnchor="middle" fontSize={12} fontWeight={700} fill="#531dab">
          {campaign}
        </text>
        <line x1={0} y1={y + AGENT_H} x2={effectiveW} y2={y + AGENT_H} stroke="#d3adf7" strokeWidth={1} />
      </g>
    );
    y += AGENT_H;

    const metrics = [
      {
        key: 'sla', label: 'Raw SLA',
        getProps: (r) => {
          const sla = r?.RawSLA    != null ? Math.round(Number(r.RawSLA))            : null;
          const tgt = r?.TargetSLA != null ? Math.round(Number(r.TargetSLA) * 100)   : null;
          const met = sla != null && tgt != null && sla >= tgt;
          return {
            fill:     sla == null ? '#f5f5f5' : met ? '#b7eb8f' : '#ffccc7',
            text:     sla != null ? `${sla}%` : '—',
            textFill: sla == null ? '#bfbfbf' : met ? '#237804' : '#a8071a',
          };
        },
      },
      {
        key: 'offered', label: 'Offered',
        getProps: (r) => ({
          fill:     r?.TotalOffered != null ? '#bae0ff' : '#f5f5f5',
          text:     r?.TotalOffered != null ? String(r.TotalOffered) : '—',
          textFill: r?.TotalOffered != null ? '#0958d9' : '#bfbfbf',
        }),
      },
      {
        key: 'handled', label: 'Handled',
        getProps: (r) => ({
          fill:     r?.TotalHandled != null ? '#d9f7be' : '#f5f5f5',
          text:     r?.TotalHandled != null ? String(r.TotalHandled) : '—',
          textFill: r?.TotalHandled != null ? '#237804' : '#bfbfbf',
        }),
      },
      {
        key: 'staffed', label: 'Staffed',
        getProps: (r) => ({
          fill:     r?.AvgStaffed != null ? '#ffe7ba' : '#f5f5f5',
          text:     r?.AvgStaffed != null ? String(r.AvgStaffed) : '—',
          textFill: r?.AvgStaffed != null ? '#ad4e00' : '#bfbfbf',
        }),
      },
      {
        key: 'ic4sla', label: 'IC 4SLA',
        getProps: (r) => ({
          fill:     r?.AvgIC4SLA != null ? '#b5f5ec' : '#f5f5f5',
          text:     r?.AvgIC4SLA != null ? String(r.AvgIC4SLA) : '—',
          textFill: r?.AvgIC4SLA != null ? '#006d75' : '#bfbfbf',
        }),
      },
      {
        key: 'notloggedin', label: 'Not Logged In',
        getProps: (r) => ({
          fill:     r?.AvgNotLoggedIn != null ? '#efdbff' : '#f5f5f5',
          text:     r?.AvgNotLoggedIn != null ? String(r.AvgNotLoggedIn) : '—',
          textFill: r?.AvgNotLoggedIn != null ? '#531dab' : '#bfbfbf',
        }),
      },
    ];

    const minsToHM = m => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

    metrics.forEach(({ key, label, getProps }) => {
      const rowY = y;
      y += MET_H + MET_GAP;
      els.push(
        <g key={key}>
          <rect x={LABEL_W} y={rowY} width={drawW} height={MET_H} fill="#fafafa" />
          {erlangData.map((r, i) => {
            const start  = hhToMins(r.HalfHourOfDay);
            const end    = start + 30;
            const x1     = toX(start);
            const w      = Math.max(2, toX(end) - x1);
            const { fill, text, textFill } = getProps(r);
            const clipId = `met-${key}-${i}`;
            return (
              <g key={i}
                onMouseEnter={e => showTip(e, [`${label}: ${text}`, `${minsToHM(start)} – ${minsToHM(end)}`])}
                onMouseMove={moveTip} onMouseLeave={hideTip}>
                <clipPath id={clipId}>
                  <rect x={x1} y={rowY + 2} width={w - 1} height={MET_H - 4} />
                </clipPath>
                <rect x={x1} y={rowY + 2} width={w - 1} height={MET_H - 4} fill={fill} rx={2} />
                {w > 8 && (
                  <text x={x1 + w / 2} y={rowY + MET_H / 2 + 4} textAnchor="middle"
                    clipPath={`url(#${clipId})`}
                    fontSize={10} fontWeight={700} fill={textFill} style={{ pointerEvents: 'none' }}>
                    {text}
                  </text>
                )}
              </g>
            );
          })}
          <rect x={0} y={rowY} width={LABEL_W} height={MET_H} fill="#ffffff" />
          <text x={LABEL_W - 6} y={rowY + MET_H / 2 + 4} textAnchor="end" fontSize={12} fill="#595959">{label}</text>
          <line x1={LABEL_W} y1={rowY + MET_H} x2={effectiveW} y2={rowY + MET_H} stroke="#e8e8e8" strokeWidth={1} />
        </g>
      );
    });
    return <g>{els}</g>;
  };

  const renderAgentRows = () => {
    let y = 0;
    return displayAgents.map(agent => {
      const data   = timelineMap[agent.ID];
      const blockY = y;
      y += agentBlockH;

      return (
        <g key={agent.ID}>
          {/* Agent name band */}
          <rect x={0} y={blockY} width={effectiveW} height={AGENT_H} fill="#dce8ff" />
          <text x={10} y={blockY + AGENT_H / 2 + 4} fontSize={12} fontWeight={700} fill="#1d39c4">
            {agent.DisplayName}
          </text>
          {agent.JobName && (
            <text x={200} y={blockY + AGENT_H / 2 + 4} fontSize={11} fill="#597ef7">
              · {agent.JobName}
            </text>
          )}
          <line x1={0} y1={blockY + AGENT_H} x2={effectiveW} y2={blockY + AGENT_H} stroke="#adc8ff" strokeWidth={1} />

          {/* Data rows */}
          {visibleRows.map(({ key, label }, ri) => {
            const rowY = blockY + AGENT_H + ri * (ROW_H + ROW_GAP);
            const bg   = ri % 2 === 0 ? '#fafafa' : '#f5f5f5';

            return (
              <g key={key}>
                <rect x={LABEL_W} y={rowY} width={drawW} height={ROW_H} fill={bg} />

                {key === 'schedule' && (data?.schedule || []).map((s, i) => {
                  const x1 = toX(toMins(s.ShiftStart)); const x2 = toX(toMins(s.ShiftEnd));
                  const w  = Math.max(2, x2 - x1);
                  return <rect key={i} x={x1} y={rowY + 4} width={w} height={ROW_H - 8}
                    fill="#91caff" fillOpacity={0.75} rx={CR}
                    onMouseEnter={e => showTip(e, [`Shift: ${fmtHM(s.ShiftStart)} – ${fmtHM(s.ShiftEnd)}`])}
                    onMouseMove={moveTip} onMouseLeave={hideTip} />;
                })}

                {key === 'punches' && (data?.punches || []).map((p, i) => {
                  const x1 = toX(toMins(p.in)); const x2 = toX(toMins(p.out));
                  const w  = Math.max(2, x2 - x1);
                  return <rect key={i} x={x1} y={rowY + 4} width={w} height={ROW_H - 8}
                    fill="#52c41a" fillOpacity={0.65} rx={CR}
                    onMouseEnter={e => showTip(e, [`Punch: ${fmtHM(p.in)} – ${fmtHM(p.out)}`])}
                    onMouseMove={moveTip} onMouseLeave={hideTip} />;
                })}

                {key === 'states' && (data?.states || []).filter(s => {
                  if (!showOnlyBreakLunch) return true;
                  const st = (s.State || '').toLowerCase();
                  return st.includes('break') || st.includes('lunch') || st.includes('meal');
                }).map((s, i) => {
                  const x1   = toX(toMins(s.StartDate));
                  const endM = s.EndDate ? toMins(s.EndDate) : rangeMax;
                  const x2   = toX(endM);
                  const w    = Math.max(2, x2 - x1);
                  const fill = stateColor(s.State);
                  const fg   = stateFg(s.State);
                  const clipId = `clip-${agent.ID}-${i}`;
                  return (
                    <g key={i}
                      onMouseEnter={e => showTip(e, [s.State, `${fmtHM(s.StartDate)} – ${s.EndDate ? fmtHM(s.EndDate) : '?'}`, ...(s.DurationSecs != null ? [fmtDur(s.DurationSecs)] : [])])}
                      onMouseMove={moveTip} onMouseLeave={hideTip}>
                      <clipPath id={clipId}>
                        <rect x={x1} y={rowY + 2} width={w} height={ROW_H - 4} />
                      </clipPath>
                      <rect x={x1} y={rowY + 2} width={w} height={ROW_H - 4} fill={fill} rx={CR} style={{ cursor: 'default' }} />
                      {w > 36 && (
                        <text x={x1 + 5} y={rowY + ROW_H / 2 + 4} clipPath={`url(#${clipId})`}
                          fontSize={11} fill={fg} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                          {s.State}
                        </text>
                      )}
                    </g>
                  );
                })}
                {(() => {
                  let hrs = null;
                  if (key === 'schedule') {
                    const grossMins = (data?.schedule || []).reduce((s, sh) => {
                      const a = toMins(sh.ShiftStart), b = toMins(sh.ShiftEnd);
                      return (a != null && b != null) ? s + (b - a) : s;
                    }, 0);
                    const netHrs = (data?.schedule || []).reduce((s, sh) => s + (Number(sh.NewTotalHrs) || 0), 0);
                    if (grossMins > 0) hrs = `${(grossMins / 60).toFixed(1)}h / ${netHrs.toFixed(1)}h`;
                  } else if (key === 'punches') {
                    const mins = (data?.punches || []).reduce((s, p) => {
                      const a = toMins(p.in), b = toMins(p.out);
                      return (a != null && b != null) ? s + (b - a) : s;
                    }, 0);
                    if (mins > 0) hrs = `${(mins / 60).toFixed(1)}h`;
                  } else if (key === 'states') {
                    const secs = (data?.states || []).reduce((s, st) => {
                      const nm = (st.State || '').toLowerCase();
                      if (nm.includes('lunch') || nm.includes('meal')) return s;
                      return s + (Number(st.DurationSecs) || 0);
                    }, 0);
                    if (secs > 0) hrs = `${(secs / 3600).toFixed(1)}h`;
                  }
                  const fullLabel = hrs != null ? `${label} (${hrs})` : label;
                  return (
                    <>
                      <rect x={0} y={rowY} width={LABEL_W} height={ROW_H} fill="#ffffff" />
                      <text x={LABEL_W - 6} y={rowY + ROW_H / 2 + 4} textAnchor="end" fontSize={11} fill="#8c8c8c">{fullLabel}</text>
                    </>
                  );
                })()}
              </g>
            );
          })}
        </g>
      );
    });
  };

  const renderGridLines = (height) => ticks.map(m => {
    const x = toX(m); const isHour = m % 60 === 0; const isHalf = !isHour && m % 30 === 0;
    return <line key={m} x1={x} y1={0} x2={x} y2={height}
      stroke={isHour ? '#d9d9d9' : isHalf ? '#e0e0e0' : '#f0f0f0'}
      strokeWidth={isHour ? 1 : isHalf ? 0.75 : 0.5} />;
  });

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Team Timeline</Typography.Title>
        {date && <Typography.Text type="secondary">{date.format('dddd, MMMM D, YYYY')}</Typography.Text>}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        <Space wrap>
          <Select
            placeholder="Select team"
            value={teamId ?? undefined}
            onChange={v => { if (v != null) setTeamId(v); }}
            options={teamOptions.map(t => ({ label: t.Name, value: t.ID }))}
            style={{ minWidth: 180 }}
          />
          <Select
            showSearch allowClear
            loading={campaignLoading}
            placeholder="Campaign (optional)"
            options={filteredCampaignOpts}
            value={campaign ?? undefined}
            onChange={v => setCampaign(v ?? null)}
            style={{ minWidth: 220 }}
            filterOption={(input, opt) => String(opt.label).toLowerCase().includes(input.toLowerCase())}
          />
          <Select
            mode="multiple" maxTagCount="responsive" placeholder="All jobs"
            value={jobIds} onChange={setJobIds} allowClear
            options={allJobs.filter(j => j.IsActive).map(j => ({ label: j.JobName, value: j.JobID }))}
            style={{ minWidth: 180 }}
          />
          <DatePicker value={date} onChange={v => { if (v) setDate(v); }} allowClear={false} />
          <Space size={4}>
            <ClockCircleOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
            <Select value={startHour} onChange={setStartHour} options={HOUR_OPTIONS} style={{ width: 110 }} size="small" />
            <span style={{ color: '#8c8c8c', fontSize: 12 }}>to</span>
            <Select value={endHour} onChange={setEndHour} options={HOUR_OPTIONS.filter(o => o.value > startHour)} style={{ width: 110 }} size="small" />
          </Space>
          <Button type="primary" loading={loading} onClick={load} disabled={!teamId}>Search</Button>
        </Space>

        <Space>
          <Checkbox checked={hidePunches}  onChange={e => setHidePunches(e.target.checked)}>
            <Typography.Text style={{ fontSize: 12 }}>Hide Punches</Typography.Text>
          </Checkbox>
          <Checkbox checked={hideSchedule} onChange={e => setHideSchedule(e.target.checked)}>
            <Typography.Text style={{ fontSize: 12 }}>Hide Schedule</Typography.Text>
          </Checkbox>
          <Checkbox checked={hideNonScheduled} onChange={e => setHideNonScheduled(e.target.checked)}>
            <Typography.Text style={{ fontSize: 12 }}>Hide Non-Scheduled</Typography.Text>
          </Checkbox>
          <Checkbox checked={hideInTraining} onChange={e => setHideInTraining(e.target.checked)}>
            <Typography.Text style={{ fontSize: 12 }}>Hide In Training</Typography.Text>
          </Checkbox>
          <Checkbox checked={showOnlyBreakLunch} onChange={e => setShowOnlyBreakLunch(e.target.checked)}>
            <Typography.Text style={{ fontSize: 12 }}>Show Only Break/Lunch</Typography.Text>
          </Checkbox>
        </Space>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
      ) : !teamId ? (
        <Empty description="Select a team to view the timeline" style={{ padding: 60 }} />
      ) : agents.length === 0 ? (
        <Empty description="No agents found — adjust filters and click Search" style={{ padding: 60 }} />
      ) : displayAgents.length === 0 ? (
        <Empty description="No agents visible — try unchecking Hide Non-Scheduled or Hide In Training" style={{ padding: 60 }} />
      ) : (
        /* Outer scroll container — handles both x and y scroll */
        <div ref={containerRef} style={{
          overflow: 'auto',
          maxHeight: 'calc(100vh - 260px)',
          border: '1px solid #e8e8e8',
          borderRadius: 8,
          background: '#fff',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}>
          {/* Inner wrapper at full SVG width so horizontal scroll is shared */}
          <div style={{ width: effectiveW, minWidth: effectiveW }}>

            {/* Sticky top: time axis + campaign metrics */}
            <div style={{
              position: 'sticky', top: 0, zIndex: 10,
              background: '#fff',
              borderBottom: '2px solid #d9d9d9',
            }}>
              <svg width={effectiveW} height={topSvgH} style={{ display: 'block', fontFamily: 'inherit' }}>
                {renderTimeAxis()}
                {renderCampaignRows()}
              </svg>
            </div>

            {/* Agent rows */}
            <svg width={effectiveW} height={bottomSvgH} style={{ display: 'block', fontFamily: 'inherit' }}>
              {renderGridLines(bottomSvgH)}
              {renderAgentRows()}
            </svg>

          </div>
        </div>
      )}

      {/* Legend */}
      <div style={{ marginTop: 12 }}>
        <Space size={16} wrap>
          {LEGEND_ITEMS
            .filter(({ label }) =>
              !(label === 'Schedule' && hideSchedule) &&
              !(label === 'Punches'  && hidePunches)
            )
            .map(({ label, color, alpha, border }) => (
              <Space key={label} size={4}>
                <span style={{
                  width: 14, height: 14, borderRadius: 2, display: 'inline-block', flexShrink: 0,
                  background: color, opacity: alpha ?? 1,
                  border: border ? '1px solid #d9d9d9' : 'none',
                }} />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>{label}</Typography.Text>
              </Space>
            ))}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>Hover for details</Typography.Text>
        </Space>
      </div>

      {/* Floating tooltip */}
      {tooltip && (
        <div style={{
          position: 'fixed',
          left: Math.min(tooltip.x + 14, window.innerWidth - 230),
          top:  tooltip.y - 10,
          background: 'rgba(0,0,0,0.8)', color: '#fff',
          padding: '5px 10px', borderRadius: 5, fontSize: 12,
          lineHeight: '20px', pointerEvents: 'none', zIndex: 9999, whiteSpace: 'pre',
        }}>
          {tooltip.lines.map((l, i) => <div key={i}>{l}</div>)}
        </div>
      )}
    </>
  );
}
